import type { ImageBleed, PageType } from 'types';
import type { ProductListItems, ProductListModule } from 'types/capi/article';

import ProductCard from '~/components/ProductCard';
import getHexBrightness from '~/utils/hexBrightness';
import { isSideBleedColumn } from '~/utils/sideBleed';

import ProductListBlock from './ProductListBlock';

const MODULE_TYPE = 'product-list';

export default function ProductList(
  props: ProductListModule & {
    bleed?: ImageBleed;
    columnCount?: number;
    modulePosition?: number;
    pageType: PageType;
  }
) {
  const items = props.items.filter(
    (item): item is NonNullable<typeof item> => item != null
  );

  if (!items.length) return null;

  switch (props.display_type) {
    case 'carousel':
      return (
        <ProductListCarousel
          {...props}
          items={items}
          modulePosition={props.modulePosition}
          pageType={props.pageType}
        />
      );
    case 'grid':
      return (
        <ProductListGrid
          {...props}
          items={items}
          modulePosition={props.modulePosition}
          pageType={props.pageType}
        />
      );
    case 'block':
      return (
        <ProductListBlock
          backgroundImage={props.background_image}
          backgroundColor={props.background_colour}
          bleed={props.bleed}
          columnCount={props.columnCount}
          modulePosition={props.modulePosition}
          product={items[0]!}
          pageType={props.pageType}
        />
      );
    default:
      return null;
  }
}

const EDGE_TO_EDGE_CONTENT_WIDTH = 'md:px-0 desktop:mx-0 desktop:max-w-none';

function isLeftBleedSideColumn(columnCount?: number, bleed?: ImageBleed) {
  return isSideBleedColumn(bleed, columnCount) && bleed === 'left';
}

function ProductListCarousel({
  header,
  description,
  items,
  modulePosition,
  background_colour = '#FAFAFA',
  bleed,
  columnCount,
  pageType
}: {
  background_colour?: string;
  bleed?: ImageBleed;
  columnCount?: number;
  description?: string;
  header?: string;
  items: ProductListItems;
  modulePosition?: number;
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const isMetadataEndAligned = isLeftBleedSideColumn(columnCount, bleed);
  const isEdgeToEdge = isSideBleedColumn(bleed, columnCount);

  return (
    <div
      className={`col-span-full w-full ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div
        className={`content-width flex h-fit flex-col items-center gap-8 ${
          isEdgeToEdge ? EDGE_TO_EDGE_CONTENT_WIDTH : ''
        }`}
      >
        {(header || description) && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            {header && <div className="text-title-lg">{header}</div>}
            {description && <div className="text-sm">{description}</div>}
          </div>
        )}
        <div
          className={`carousel-width no-scrollbar flex shrink-0 justify-start gap-2 md:gap-6 ${
            isEdgeToEdge ? 'md:mx-0 md:px-0' : ''
          }`}
        >
          {items.map((product, productIndex) => {
            const className = `h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px] ${
              shouldCentreItems ? 'md:first:ml-auto md:last:mr-auto' : ''
            }`;

            if (product.type === 'inline-external') {
              return (
                <ProductCard.InlineExternal
                  cardSize="small"
                  name={`PRODUCT_CARD_${product.name}`}
                  className={className}
                  displayType="carousel"
                  imageLoading="eager"
                  isMetadataEndAligned={isMetadataEndAligned}
                  key={product.url}
                  modulePosition={modulePosition}
                  moduleType={MODULE_TYPE}
                  padImage
                  pagePosition="Product Carousel"
                  position={productIndex + 1}
                  product={product}
                />
              );
            }

            return product.type === 'external' ? (
              <ProductCard.External
                allowNotifications
                cardSize="small"
                name={`PRODUCT_CARD_${product.name}`}
                className={className}
                imageLoading="eager"
                isMetadataEndAligned={isMetadataEndAligned}
                key={product.id}
                modulePosition={modulePosition}
                displayType="carousel"
                moduleType={MODULE_TYPE}
                openPdp={false}
                padImage
                pagePosition="Product Carousel"
                position={productIndex + 1}
                product={product}
                pageType={pageType}
              />
            ) : (
              <ProductCard.Internal
                allowNotifications
                cardSize="small"
                name={`PRODUCT_CARD_${header}`}
                className={className}
                imageLoading="eager"
                isMetadataEndAligned={isMetadataEndAligned}
                key={product.id}
                modulePosition={modulePosition}
                displayType="carousel"
                moduleType={MODULE_TYPE}
                padImage
                pageType={pageType}
                pagePosition="Product Carousel"
                position={productIndex + 1}
                product={product}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const wideScreenColumnsByItemCount: Record<number, string> = {
  2: 'min-[2000px]:grid-cols-2',
  3: 'min-[2000px]:grid-cols-3',
  4: 'min-[2000px]:grid-cols-4'
};

function getGridColumnsClass({
  columnCount,
  isBleedColumn,
  itemCount
}: {
  columnCount?: number;
  isBleedColumn: boolean;
  itemCount: number;
}) {
  if (columnCount === 3 && !isBleedColumn) return 'md:grid-cols-2';
  if (columnCount === 3 && itemCount === 1) return 'md:grid-cols-1';
  if (columnCount === 3)
    return wideScreenColumnsByItemCount[Math.min(itemCount, 4)];
  if (itemCount < 4) return `md:grid-cols-${itemCount}`;

  return 'lg:grid-cols-4';
}

function ProductListGrid({
  background_colour = '#2A2528',
  bleed,
  columnCount,
  description,
  header,
  items,
  modulePosition,
  pageType
}: {
  background_colour?: string;
  bleed?: ImageBleed;
  columnCount?: number;
  description?: string;
  header?: string;
  items: ProductListItems;
  modulePosition?: number;
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const isBleedColumn = bleed !== undefined && bleed !== 'none';
  const isMetadataEndAligned = isLeftBleedSideColumn(columnCount, bleed);
  const isEdgeToEdge = isSideBleedColumn(bleed, columnCount);

  return (
    <div
      className={`w-full ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div
        className={`content-width flex flex-col items-center gap-6 max-md:px-0 ${
          isEdgeToEdge ? EDGE_TO_EDGE_CONTENT_WIDTH : ''
        }`}
      >
        {(header || description) && (
          <div className="flex flex-col items-center gap-2 text-center">
            {header && <h2 className="text-title-lg">{header}</h2>}
            {description && (
              <p
                className={`text-sm ${bgBrightness === 'dark' ? 'text-gray-300' : 'text-black'}`}
              >
                {description}
              </p>
            )}
          </div>
        )}
        <div
          className={`w-full ${shouldCentreItems ? 'flex justify-center' : ''}`}
        >
          <div
            className={`grid grid-cols-2 gap-x-2 gap-y-4 bg-inherit md:gap-6 ${getGridColumnsClass(
              { columnCount, isBleedColumn, itemCount: items.length }
            )}`}
          >
            {items.map((product, productIndex) => {
              const className = 'mx-auto h-auto shrink-0';

              switch (product.type) {
                case 'internal':
                  return (
                    <ProductCard.Internal
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${header}`}
                      className={className}
                      imageLoading="eager"
                      isMetadataEndAligned={isMetadataEndAligned}
                      key={product.id}
                      modulePosition={modulePosition}
                      displayType="grid"
                      moduleType={MODULE_TYPE}
                      padImage
                      pageType={pageType}
                      pagePosition="Product Grid"
                      position={productIndex + 1}
                      product={product}
                    />
                  );
                case 'external':
                  return (
                    <ProductCard.External
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      imageLoading="eager"
                      isMetadataEndAligned={isMetadataEndAligned}
                      key={product.id}
                      modulePosition={modulePosition}
                      displayType="grid"
                      moduleType={MODULE_TYPE}
                      openPdp={false}
                      padImage
                      pagePosition="Product Grid"
                      position={productIndex + 1}
                      product={product}
                      pageType={pageType}
                    />
                  );
                case 'inline-external':
                  return (
                    <ProductCard.InlineExternal
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      displayType="grid"
                      imageLoading="eager"
                      isMetadataEndAligned={isMetadataEndAligned}
                      key={product.url}
                      modulePosition={modulePosition}
                      moduleType={MODULE_TYPE}
                      padImage
                      pagePosition="Product Grid"
                      position={productIndex + 1}
                      product={product}
                    />
                  );
                default:
                  return <></>;
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
